import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { GfaAuthServiceRequestInitV2 } from '@volkswagen-onehub/gfa-auth-service/src/v2/gfa-auth-service-v2';
import { InvitationAction, InvitationRequestBody, Invitations, RectivateAndReovkeSURightsRequestBody } from './types';

const getRequestOptions = async (stage: string, method: string) => {
    const requestHeaders: HeadersInit = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    };

    const options: GfaAuthServiceRequestInitV2 = {
        resourceHostId: `myvw-gupis-${stage}`,
        method: method,
        headers: requestHeaders,
        credentials: 'same-origin',
        mode: 'cors',
        redirect: 'follow',
        referrer: 'no-referrer',
    };

    return options;
};

export const fetchGupis = async <T>(authService: IAuthServiceV2, url: string, options: RequestInit): Promise<T> => {
    return authService
        .fetch(url, options)
        .then((response: Response) => {
            if (!response.ok) {
                throw new Error(`Fetch Gupis request failed with ${response.status}`);
            }

            if (response.status === 201 || response.status === 204) {
                return null as unknown as Promise<T>;
            }

            return response.json();
        })
        .then((data: any) => {
            return data;
        })
        .catch((error: any) => {
            if (error instanceof Error) {
                console.error(`Fetch Gupis request failed with ${error.message}`);
            } else {
                console.error(`Fetch Gupis request: ${error}`);
            }
        });
};

export const fetchInvitations = async (
    authService: IAuthServiceV2,
    gdcType: string,
    stage: string,
): Promise<Invitations> => {
    const options = await getRequestOptions(stage, 'GET');
    return fetchGupis(authService, `v1/guestuserplus/invitations?gdc=${gdcType}`, options);
};

export const sendInvitation = async (
    authService: IAuthServiceV2,
    stage: string,
    invitationBody: InvitationRequestBody,
    gdcType: string,
): Promise<Invitations> => {
    const options = await getRequestOptions(stage, 'POST');
    options.body = JSON.stringify({
        '2fmToken': invitationBody.fmToken,
        vin: invitationBody.vin,
        guSsoId: invitationBody.inviteeID,
    });

    return fetchGupis(authService, `v1/guestuserplus/invitations?gdc=${gdcType}`, options);
};

const invitationAction = async (
    authService: IAuthServiceV2,
    stage: string,
    invitationID: string,
    requestMethod: 'PUT' | 'DELETE',
    gdcType: string,
    action?: 'reject' | 'accept',
): Promise<InvitationAction | null> => {
    const options = await getRequestOptions(stage, requestMethod);
    return fetchGupis(
        authService,
        `v1/guestuserplus/invitations/${invitationID}${action ? '/' + action : ''}?gdc=${gdcType}`,
        options,
    );
};

export const rejectInvitation = async (
    authService: IAuthServiceV2,
    stage: string,
    invitationID: string,
    gdcType: string,
): Promise<InvitationAction | null> => {
    return invitationAction(authService, stage, invitationID, 'PUT', gdcType, 'reject');
};

export const acceptInvitation = async (
    authService: IAuthServiceV2,
    stage: string,
    invitationID: string,
    gdcType: string,
): Promise<InvitationAction | null> => {
    return invitationAction(authService, stage, invitationID, 'PUT', gdcType, 'accept');
};

export const revokeInvitation = async (
    authService: IAuthServiceV2,
    stage: string,
    invitationID: string,
    gdcType: string,
): Promise<void> => {
    const options = await getRequestOptions(stage, 'DELETE');
    return fetchGupis(authService, `v1/guestuserplus/invitations/${invitationID}?gdc=${gdcType}`, options);
};

export const revokeSURights = async (
    authService: IAuthServiceV2,
    stage: string,
    reactivationBody: RectivateAndReovkeSURightsRequestBody,
    gdcType: string,
): Promise<void> => {
    const options = await getRequestOptions(stage, 'DELETE');
    options.body = JSON.stringify({
        vin: reactivationBody.vin,
        guSsoId: reactivationBody.guSsoId,
    });
    return fetchGupis(authService, `v1/guestuserplus/permissions/revoke?gdc=${gdcType}`, options);
};
